import React, { useState } from 'react'
import { Camera } from 'lucide-react'
import * as Popover from '@radix-ui/react-popover'
import { useTenantStore } from 'src/enviroment/store/tenentStore'
import { enqueueSnackbar } from 'notistack'
import { SubscriptionPlanType } from '../../types/subscription'
import { cn } from '../../enviroment/lib/utils'
import { useChatStore } from '../../enviroment/store/chatStore'
import { useShallow } from 'zustand/react/shallow'
import { logLimitHit } from '../../enviroment/api/metrics'

interface CompanyLogoUploadProps {
  currentLogo: string | null
  spaceName: string
  onLogoSelect: (file: File) => void
  onLogoRemove: () => void
}

const CompanyLogoUpload = ({ currentLogo, spaceName, onLogoSelect, onLogoRemove }: CompanyLogoUploadProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const currentPlan = useTenantStore((state) => state.currentTenant.plan)
  const { setIsExpanded, upsertMessageByType } = useChatStore(
    useShallow((s) => ({ setIsExpanded: s.setIsExpanded, upsertMessageByType: s.upsertMessageByType })),
  )

  let hoverTimeout: NodeJS.Timeout

  const isLogoUpdateAllowed = currentPlan !== SubscriptionPlanType.FREE

  const handleMouseEnter = () => {
    clearTimeout(hoverTimeout)
    setIsPopoverOpen(true)
  }

  const handleMouseLeave = () => {
    hoverTimeout = setTimeout(() => {
      setIsPopoverOpen(false)
    }, 100)
  }

  const validateFile = (file: File) => {
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml']

    if (!validTypes.includes(file.type)) {
      enqueueSnackbar('Please upload a PNG, JPG or SVG file', { variant: 'error' })
      return false
    }

    if (file.size > 2 * 1024 * 1024) {
      enqueueSnackbar('Please upload an image smaller than 2MB', { variant: 'error' })
      return false
    }

    return true
  }

  const handleFileSelect = () => {
    if (!isLogoUpdateAllowed) {
      logLimitHit('WHITE_LABEL', { plan: currentPlan })
      upsertMessageByType('white-label')
      setIsExpanded(true)
      return
    }

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/png,image/jpeg,image/svg+xml'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return

      const isValid = await validateFile(file)
      if (isValid) {
        onLogoSelect(file)
      }
    }
    input.click()
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div className="flex items-start">
      <div className="relative">
        <div
          className={cn(
            'group relative h-20 w-20 overflow-hidden rounded-lg transition-colors',
            currentLogo ? 'bg-gray-800' : 'bg-gray-800',
            isLogoUpdateAllowed ? 'cursor-pointer' : 'cursor-not-allowed',
          )}
          onClick={handleFileSelect}
        >
          {currentLogo ? (
            <img src={currentLogo} alt="Company logo" className="h-full w-full object-contain" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-900 text-2xl text-emerald-400">
              {getInitials(spaceName)}
            </div>
          )}
        </div>

        {isLogoUpdateAllowed && (
          <Popover.Root open={isPopoverOpen}>
            <Popover.Trigger asChild>
              <div
                className="absolute -bottom-2 -right-2 cursor-pointer rounded-md border border-emerald-500 bg-gray-800 p-1"
                onClick={handleFileSelect}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <Camera size={16} className="text-emerald-400" />
              </div>
            </Popover.Trigger>

            <Popover.Portal>
              <Popover.Content
                className="z-50 rounded-lg bg-gray-700 p-3 shadow-md"
                sideOffset={5}
                side="right"
                align="center"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div className="space-y-2 text-sm text-white">
                  <div>PNG, JPG</div>
                  <div>Recommended: 200×200px</div>
                  <div>Max: 2MB</div>
                </div>
                <Popover.Arrow className="fill-gray-700" />
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        )}
      </div>
    </div>
  )
}

export default CompanyLogoUpload
